import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { toast } from "sonner";
import { Plus, Loader2, FileSignature, X, ExternalLink, Upload, Copy } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_COLORS = {
  created: "bg-stone-100 text-stone-700",
  sent: "bg-blue-50 text-blue-700",
  viewed: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  declined: "bg-rose-50 text-rose-700",
};

export default function EsignDocuseal() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, c, t] = await Promise.all([
        axios.get(`${API_BASE}/esign-docuseal`),
        axios.get(`${API_BASE}/clients`),
        axios.get(`${API_BASE}/esign-docuseal/templates?limit=50`),
      ]);
      setRows(r.data);
      setClients(c.data);
      const tplList = t.data?.data || t.data || [];
      setTemplates(Array.isArray(tplList) ? tplList : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "—";

  const [entryMode, setEntryMode] = useState("upload");   // v68.33 — "upload" | "template"
  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="esign-docuseal-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="overline mb-2">Signatures · DocuSeal</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>DocuSeal envelopes</h1>
          <p className="text-[var(--ink-2)] mt-2">
            Production-grade eSignature backed by DocuSeal with full audit trail.
          </p>
        </div>
      </div>

      {/* v68.33 — Unified entry: pick Upload or Template, panel below reflects the choice. */}
      <div className="inline-flex rounded-md border border-[var(--border)] bg-white p-1 mb-4"
           role="tablist" data-testid="esign-ds-entry-tabs">
        <button onClick={() => setEntryMode("upload")}
                role="tab" aria-selected={entryMode === "upload"}
                data-testid="esign-ds-entry-upload"
                className={`px-4 py-1.5 text-[12px] rounded ${entryMode === "upload"
                  ? "bg-[var(--ink)] text-[var(--paper)]"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
          Upload & sign
        </button>
        <button onClick={() => setEntryMode("template")}
                role="tab" aria-selected={entryMode === "template"}
                data-testid="esign-ds-entry-template"
                disabled={!templates.length}
                className={`px-4 py-1.5 text-[12px] rounded disabled:opacity-40 ${entryMode === "template"
                  ? "bg-[var(--ink)] text-[var(--paper)]"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
          Use template
        </button>
      </div>

      {entryMode === "template" && templates.length > 0 && (
        <div className="mb-6">
          <button onClick={() => setShow(true)} className="btn-primary" data-testid="esign-ds-create-btn">
            <Plus size={14} /> New envelope from template
          </button>
        </div>
      )}
      {entryMode === "template" && !templates.length && !loading && (
        <div className="card p-6 mb-6 border-amber-200 bg-amber-50/30">
          <div className="text-[14px] text-stone-700">
            No templates found in your DocuSeal account. Create one at{" "}
            <a href="https://console.docuseal.com/templates" target="_blank" rel="noopener noreferrer"
               className="underline">console.docuseal.com/templates</a> then refresh this page.
          </div>
        </div>
      )}

      {entryMode === "upload" && (
        <QuickSendCard clients={clients} onSent={reload} />
      )}

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="esign-ds-empty">
          <FileSignature size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No DocuSeal envelopes yet</div>
          <p className="text-[var(--ink-3)] text-[13px]">Click "New envelope" to send a template for signature.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>Title</th><th>Client</th><th>Signer</th><th>Status</th><th>Created</th><th>Completed</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.envelope_id} data-testid={`esign-ds-row-${r.envelope_id}`}>
                  <td className="font-medium text-[var(--ink)]">{r.title}</td>
                  <td>{clientName(r.client_id)}</td>
                  <td className="text-[13px]">{r.signer_email}</td>
                  <td>
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_COLORS[r.status] || "bg-stone-100 text-stone-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="text-[13px] text-[var(--ink-2)]">{fmtDate(r.created_at)}</td>
                  <td className="text-[13px] text-[var(--ink-2)]">{fmtDate(r.completed_at)}</td>
                  <td>
                    {r.embed_url && (
                      <a href={r.embed_url} target="_blank" rel="noopener noreferrer"
                         className="text-[var(--accent)] hover:underline text-[12px] inline-flex items-center gap-1"
                         data-testid={`esign-ds-open-${r.envelope_id}`}>
                        Open <ExternalLink size={11} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {show && (
        <CreateEnvelopeModal
          clients={clients}
          templates={templates}
          onClose={() => setShow(false)}
          onCreated={() => { setShow(false); reload(); }}
        />
      )}
    </div>
  );
}

function CreateEnvelopeModal({ clients, templates, onClose, onCreated }) {
  const [clientId, setClientId] = useState(clients[0]?.client_id || "");
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [signerEmail, setSignerEmail] = useState("");
  const [signerName, setSignerName] = useState("");
  const [title, setTitle] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId || !templateId || !signerEmail) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/esign-docuseal`, {
        client_id: clientId,
        template_id: Number(templateId),
        signer_email: signerEmail,
        signer_name: signerName || undefined,
        title: title || undefined,
        send_email: sendEmail,
      });
      toast.success(sendEmail ? "Envelope created and sent to signer" : "Envelope created (no email)");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="esign-ds-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl">New DocuSeal envelope</h3>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700">
            <X size={18} />
          </button>
        </div>
        <div>
          <label className="overline block mb-1.5">Client</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} required
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]"
                  data-testid="esign-ds-client-select">
            {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-1.5">DocuSeal template</label>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} required
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]"
                  data-testid="esign-ds-template-select">
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name || `Template ${t.id}`}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-1.5">Signer email</label>
          <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} required
                 className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]"
                 data-testid="esign-ds-signer-email" />
        </div>
        <div>
          <label className="overline block mb-1.5">Signer name <span className="text-stone-400">(optional)</span></label>
          <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                 className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]" />
        </div>
        <div>
          <label className="overline block mb-1.5">Title <span className="text-stone-400">(optional)</span></label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Engagement Letter — 2024"
                 className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px]" />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-[var(--ink-2)] cursor-pointer">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                 data-testid="esign-ds-send-email" />
          Send signing email to signer immediately
        </label>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="submit" disabled={busy || !signerEmail} className="btn-primary flex-1 justify-center"
                  data-testid="esign-ds-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Create envelope"}
          </button>
        </div>
      </form>
    </div>
  );
}


/* v68.32 — Drag-and-drop quick-send with multi-signer + "sign yourself"
   + job-stage attach + optional visual field builder. */
function QuickSendCard({ clients, onSent }) {
  const { state } = useAuth();
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [clientId, setClientId] = useState("");
  const [signers, setSigners] = useState([{ email: "", name: "", role: "Client" }]);
  const [title, setTitle] = useState("");
  const [builderMode, setBuilderMode] = useState(false);
  // Job/stage attach
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [stageIndex, setStageIndex] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);       // v68.33 blob URL for PDF preview
  const [embedSignerEmail, setEmbedSignerEmail] = useState(null);   // v68.33 inline-sign modal

  // Build + revoke blob URL for the picked PDF preview
  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    axios.get(`${API_BASE}/jobs`).then(({ data }) => setJobs(data || [])).catch(() => {});
  }, []);

  // Auto-fill first signer from picked client
  useEffect(() => {
    if (!clientId) return;
    const c = clients.find((x) => x.client_id === clientId);
    if (!c) return;
    setSigners((cur) => {
      const [first, ...rest] = cur;
      return [{ ...first,
                 email: first.email || c.primary_contact_email || "",
                 name: first.name || c.name || "" },
               ...rest];
    });
  }, [clientId, clients]);

  const updSigner = (i, patch) =>
    setSigners((cur) => cur.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addSigner = () =>
    setSigners((cur) => [...cur, { email: "", name: "", role: `Signer ${cur.length + 1}` }]);
  const removeSigner = (i) =>
    setSigners((cur) => (cur.length === 1 ? cur : cur.filter((_, j) => j !== i)));

  // v68.34 — Sequential signing preview: non-individual client + >=2 signers.
  const pickedClient = clients.find((c) => c.client_id === clientId);
  const willEnforceOrder = !!pickedClient
    && (pickedClient.type || "individual") !== "individual"
    && signers.filter((s) => (s.email || "").includes("@")).length > 1;
  const addMe = () => {
    const my = state?.user;
    if (!my?.email) { toast.error("No email on your profile"); return; }
    if (signers.some((s) => (s.email || "").toLowerCase() === my.email.toLowerCase())) {
      toast.info("You're already a signer");
      return;
    }
    setSigners((cur) => [...cur, { email: my.email, name: my.name || "Me",
                                     role: cur.length === 0 ? "Client" : "Firm" }]);
  };

  const onPick = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Only PDF files"); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error("PDF exceeds 25 MB"); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ""));
  };

  const stagesFor = (jid) => (jobs.find((j) => j.job_id === jid)?.stages || []);

  const send = async () => {
    const valid = signers.filter((s) => s.email.includes("@"));
    if (!file || !valid.length) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("pdf", file);
      form.append("signers", JSON.stringify(valid));
      if (title.trim()) form.append("title", title.trim());
      if (clientId) form.append("client_id", clientId);
      if (jobId) form.append("job_id", jobId);
      if (jobId && stageIndex !== "") form.append("stage_index", String(stageIndex));
      if (builderMode) form.append("builder_mode", "true");
      const { data } = await axios.post(`${API_BASE}/esign-docuseal/quick-send`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.mode === "builder") {
        toast.success("Opening DocuSeal builder — drag your fields, then come back");
      } else {
        toast.success(`Sent to ${valid.length} signer${valid.length > 1 ? "s" : ""}`);
      }
      onSent && onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sendFromTemplate = async () => {
    if (!result?.envelope_id) return;
    const valid = signers.filter((s) => s.email.includes("@"));
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/esign-docuseal/quick-send/${result.envelope_id}/send-from-template`,
        { signers: valid },
      );
      setResult({ ...result, ...data, mode: "quick_send" });
      toast.success(`Sent to ${valid.length} signer${valid.length > 1 ? "s" : ""}`);
      onSent && onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyUrl = () => {
    if (!result?.signing_url) return;
    navigator.clipboard.writeText(result.signing_url);
    toast.success("Signing URL copied");
  };

  return (
    <div className="card p-5 mb-6" data-testid="esign-ds-quicksend">
      <div className="flex items-start gap-4 flex-wrap">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onPick(e.dataTransfer.files?.[0]);
          }}
          className={`flex-1 min-w-[280px] border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver ? "border-[var(--accent)] bg-[var(--accent-light)]/40"
                     : file ? "border-[var(--moss)] bg-[var(--moss)]/5"
                            : "border-[var(--border)] hover:border-[var(--ink-3)]"
          }`}
          data-testid="esign-ds-quicksend-drop"
        >
          <Upload size={22} className={`mx-auto mb-2 ${file ? "text-[var(--moss)]" : "text-[var(--ink-3)]"}`} />
          {file ? (
            <div className="text-[13px]">
              <div className="font-medium text-[var(--ink)]">{file.name}</div>
              <div className="text-[11px] text-[var(--ink-3)] mt-1">
                {Math.round(file.size / 1024)} KB · ready to send
              </div>
              {previewUrl && (
                <embed
                  src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  type="application/pdf"
                  data-testid="esign-ds-quicksend-preview"
                  className="mt-3 w-full h-40 rounded border border-[var(--border)] bg-white"
                />
              )}
              <button
                onClick={() => { setFile(null); setResult(null); }}
                data-testid="esign-ds-quicksend-clear"
                className="mt-2 text-[11px] text-[var(--ink-3)] hover:text-[var(--accent)] underline"
              >Clear</button>
            </div>
          ) : (
            <>
              <div className="text-[13px] text-[var(--ink)] font-medium">Drag a PDF here</div>
              <div className="text-[11px] text-[var(--ink-3)] mt-1">
                or{" "}
                <label className="underline cursor-pointer">
                  browse
                  <input
                    type="file" accept="application/pdf" className="hidden"
                    data-testid="esign-ds-quicksend-file"
                    onChange={(e) => onPick(e.target.files?.[0])}
                  />
                </label>
                . Auto-detects tags — or drop signature fields on page 1.
              </div>
            </>
          )}
        </div>
        <div className="flex-1 min-w-[320px] space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Client (optional)</label>
              <select
                value={clientId} onChange={(e) => setClientId(e.target.value)}
                className="w-full bg-white border border-[var(--border)] rounded px-2 py-2 text-[13px]"
                data-testid="esign-ds-quicksend-client"
              >
                <option value="">— None —</option>
                {clients.map((c) => (
                  <option key={c.client_id} value={c.client_id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                     data-testid="esign-ds-quicksend-title"
                     className="w-full bg-white border border-[var(--border)] rounded px-2 py-2 text-[13px]"
                     placeholder="Engagement letter"/>
            </div>
          </div>
          {/* Signers list */}
          <div>
            <div className="flex items-center justify-between mt-1 mb-1">
              <label className="text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
                Signers ({signers.length})
              </label>
              <div className="flex items-center gap-2">
                <button onClick={addMe} className="text-[10px] text-[var(--accent)] hover:underline"
                        data-testid="esign-ds-quicksend-add-me">+ Add me as signer</button>
                <button onClick={addSigner} className="text-[10px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                        data-testid="esign-ds-quicksend-add-signer">+ Add signer</button>
              </div>
            </div>
            {willEnforceOrder && (
              <div className="mb-1.5 rounded border border-[var(--accent)]/40 bg-[var(--accent-light)]/40
                              px-2 py-1.5 text-[11px] text-[var(--ink-2)]"
                   data-testid="esign-ds-quicksend-order-hint">
                <span className="font-medium text-[var(--ink)]">Sequential signing</span>
                {" — "}client signs first, firm co-signature unlocks after
                {" ("}non-individual client{")"}.
              </div>
            )}
            <div className="space-y-1">
              {signers.map((s, i) => (
                <div key={i} className="flex items-center gap-1" data-testid={`esign-ds-signer-${i}`}>
                  <input type="email" value={s.email}
                         onChange={(e) => updSigner(i, { email: e.target.value })}
                         placeholder="signer@example.com"
                         className="flex-1 bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]"
                         data-testid={`esign-ds-signer-email-${i}`}/>
                  <input value={s.name}
                         onChange={(e) => updSigner(i, { name: e.target.value })}
                         placeholder="Name"
                         className="w-28 bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]"/>
                  <input value={s.role}
                         onChange={(e) => updSigner(i, { role: e.target.value })}
                         placeholder="Role"
                         className="w-24 bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]"/>
                  {signers.length > 1 && (
                    <button onClick={() => removeSigner(i)}
                            data-testid={`esign-ds-signer-remove-${i}`}
                            className="text-[var(--ink-3)] hover:text-[var(--accent)]"><X size={12}/></button>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Job/stage attach */}
          <details className="text-[12px] pt-1">
            <summary className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]">
              Attach to job stage <span className="text-[10px]">(auto-advance on sign)</span>
            </summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <select value={jobId} onChange={(e) => { setJobId(e.target.value); setStageIndex(""); }}
                      className="bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]"
                      data-testid="esign-ds-quicksend-job">
                <option value="">— No job —</option>
                {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}
              </select>
              <select value={stageIndex} onChange={(e) => setStageIndex(e.target.value)}
                      disabled={!jobId}
                      className="bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px] disabled:opacity-50"
                      data-testid="esign-ds-quicksend-stage">
                <option value="">— Stage —</option>
                {stagesFor(jobId).map((s, i) => <option key={i} value={i}>{i + 1}. {s.name}</option>)}
              </select>
            </div>
          </details>
          {/* Builder mode toggle */}
          <label className="flex items-center gap-2 text-[11px] text-[var(--ink-2)] cursor-pointer pt-1"
                 data-testid="esign-ds-quicksend-builder-label">
            <input type="checkbox" checked={builderMode}
                   onChange={(e) => setBuilderMode(e.target.checked)}
                   data-testid="esign-ds-quicksend-builder"/>
            Drop fields visually in DocuSeal's builder before sending
          </label>
          <button
            onClick={send}
            disabled={busy || !file || !signers.some((s) => s.email.includes("@"))
                       || (result?.mode === "builder")}
            data-testid="esign-ds-quicksend-send"
            className="btn-primary w-full mt-1 disabled:opacity-50"
          >
            {busy ? <Loader2 size={13} className="animate-spin"/> : <FileSignature size={13}/>}
            {builderMode ? "Upload & open builder" : "Send for signature"}
          </button>
        </div>
      </div>
      {result?.mode === "builder" && (
        <div className="mt-4 rounded-md border border-[var(--accent)]/40 bg-[var(--accent-light)]/40 p-3 space-y-2"
             data-testid="esign-ds-quicksend-builder-result">
          <div className="text-[13px] text-[var(--ink)] font-medium">
            Template created. Drag fields in the DocuSeal builder, then click below to send.
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={result.builder_url} target="_blank" rel="noopener noreferrer"
               className="btn-secondary text-[12px]"
               data-testid="esign-ds-quicksend-builder-open">
              <ExternalLink size={12}/> Open builder
            </a>
            <button onClick={sendFromTemplate} disabled={busy}
                    className="btn-primary text-[12px]"
                    data-testid="esign-ds-quicksend-builder-send">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <FileSignature size={12}/>}
              Send now
            </button>
          </div>
        </div>
      )}
      {result?.status === "sent" && (
        <div className="mt-4 rounded-md border border-[var(--moss)]/40 bg-[var(--moss)]/5 p-3 flex items-center gap-3 flex-wrap"
             data-testid="esign-ds-quicksend-result">
          <FileSignature size={14} className="text-[var(--moss)]" />
          <div className="flex-1 text-[12px] text-[var(--ink)] truncate min-w-[200px]">
            Sent to {(signers.filter((s) => s.email.includes("@"))).length} signer(s) · envelope{" "}
            <span className="font-mono text-[var(--ink-3)]">{result.envelope_id.slice(-8)}</span>
            {result.signing_order === "sequential" && (
              <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium
                                bg-[var(--accent-light)] text-[var(--accent)] align-middle"
                    data-testid="esign-ds-quicksend-order-badge">
                sequential · client first
              </span>
            )}
          </div>
          {/* v68.33 — Sign in-app when the current user is a signer */}
          {result.signing_urls && state?.user?.email &&
            result.signing_urls[state.user.email.toLowerCase()] && (
            <button
              onClick={() => setEmbedSignerEmail(state.user.email.toLowerCase())}
              className="btn-primary text-[11px]"
              data-testid="esign-ds-quicksend-sign-inapp"
            >
              <FileSignature size={11}/> Sign in-app
            </button>
          )}
          {result.signing_url && (
            <>
              <a href={result.signing_url} target="_blank" rel="noopener noreferrer"
                 className="btn-secondary text-[11px]"
                 data-testid="esign-ds-quicksend-open">
                <ExternalLink size={11}/> Open
              </a>
              <button onClick={copyUrl} className="btn-secondary text-[11px]"
                      data-testid="esign-ds-quicksend-copy">
                <Copy size={11}/> Copy URL
              </button>
            </>
          )}
        </div>
      )}
      {/* v68.33 — Embedded signing modal (iframe) */}
      {embedSignerEmail && result?.signing_urls?.[embedSignerEmail] && (
        <div className="fixed inset-0 z-[70] bg-[var(--ink)]/60 flex items-center justify-center p-4"
             onClick={() => setEmbedSignerEmail(null)}
             data-testid="esign-ds-quicksend-inapp-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)]">
              <div className="text-[13px] text-[var(--ink)]">
                Signing as <span className="font-mono">{embedSignerEmail}</span>
              </div>
              <button onClick={() => setEmbedSignerEmail(null)}
                      className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                      data-testid="esign-ds-quicksend-inapp-close"><X size={16}/></button>
            </div>
            <iframe
              title="DocuSeal signing"
              src={result.signing_urls[embedSignerEmail]}
              className="flex-1 border-0 w-full"
              data-testid="esign-ds-quicksend-inapp-iframe"
              allow="camera; microphone; clipboard-write"
            />
          </div>
        </div>
      )}
    </div>
  );
}

